import { useQuery } from "@tanstack/react-query";
import { Archive, LayoutDashboard, Shield, Users } from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { fetchCurrentAdmin } from "../lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const navItems = [
  { to: "/admin", label: "概览", icon: LayoutDashboard, end: true },
  { to: "/admin/members", label: "成员", icon: Users },
];

export function AdminLayout() {
  const current = useQuery({
    queryKey: ["admin", "me"],
    queryFn: fetchCurrentAdmin,
  });

  if (current.isLoading) {
    return <div className="page-shell py-12 text-sm text-muted-foreground">正在检查后台权限...</div>;
  }

  if (current.isError) {
    return (
      <div className="page-shell py-12">
        <Card className="max-w-2xl rounded-2xl">
          <CardContent className="p-6">
            <div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-soft text-ink">
              <Shield size={22} aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-semibold text-ink">需要后台访问权限</h1>
            <Alert className="mt-4">
              <AlertTitle>后台权限未通过</AlertTitle>
              <AlertDescription>
                请先在 Cloudflare Access 中保护 `/admin/*` 和 `/api/admin/*`，并确认请求带有访问者身份。
                应用内部还会读取 `admin_users` 表判断角色。
              </AlertDescription>
            </Alert>
            <Button asChild variant="outline" className="mt-6 rounded-full">
              <Link to="/">返回查看页</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const admin = current.data;

  if (!admin) {
    return <div className="page-shell py-12 text-sm text-muted-foreground">后台权限状态异常，请刷新重试。</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r bg-white lg:block">
        <div className="flex h-16 items-center gap-3 border-b px-5">
          <span className="flex size-10 items-center justify-center rounded-2xl bg-ink text-white">
            <Archive size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold text-ink">成员档案后台</div>
            <div className="truncate text-xs text-muted-foreground">{admin.login}</div>
          </div>
        </div>
        <nav className="space-y-1 p-3">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {({ isActive }) => (
                <span
                  className={`flex min-h-11 items-center gap-3 rounded-2xl px-3 text-sm font-medium transition-colors ${
                    isActive ? "bg-ink text-white" : "text-body hover:bg-soft"
                  }`}
                >
                  <item.icon size={18} aria-hidden="true" />
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 border-b bg-white/95 backdrop-blur">
          <div className="flex min-h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <Button asChild variant="ghost" className="rounded-full">
              <Link to="/">查看前台</Link>
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield size={16} aria-hidden="true" />
              {admin.role}
              {admin.bootstrap ? " · 初始化模式" : ""}
            </div>
          </div>
        </header>
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
